import { useToast } from '@chakra-ui/react';
import { PostDataWrapper } from '@diamond/shared/data-access';
import { CreateOrderResponse, Order } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  LIST_FEATURE_FLAGS,
  trackEvent,
  useFeatureFlag,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { NextRouter } from 'next/router';
import { useState } from 'react';

import { navigateSafely } from '../../navigation/navigation';

interface ProductPayload {
  id: string;
  quantity: number;
  note?: string;
}

interface UseOrderActionsParams {
  router: NextRouter;
  accessToken: string;
  selectedShipToId?: string;
  warehouseId?: string;
  products: ProductPayload[];
  order?: Order;
}

interface UseOrderActionsResult {
  handleBuyAgain: () => Promise<void>;
  handleNavigateToShoppingList: () => Promise<void>;
  handleNavigateHome: () => Promise<void>;
  handleNavigateMyOrder: () => Promise<void>;
  handleCopyToClipboard: (value: string) => Promise<boolean>;
  isSubmitting: boolean;
}

export const useOrderActions = ({
  router,
  accessToken,
  selectedShipToId,
  warehouseId,
  products,
  order,
}: UseOrderActionsParams): UseOrderActionsResult => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { postData } = PostDataWrapper('/order', accessToken, false, 'buyer');

  const isNewCheckoutEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_NEW_CHECKOUT,
    false
  );

  const handleBuyAgain = async () => {
    try {
      setIsSubmitting(true);

      if (!accessToken) {
        showToast(toast, 'error', 'Session tidak valid, silakan login kembali');
        setIsSubmitting(false);
        return;
      }

      const warehouseIdToUse = warehouseId ?? selectedShipToId;

      if (!warehouseIdToUse) {
        showToast(toast, 'error', 'Warehouse tidak ditemukan');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        warehouse_id: warehouseIdToUse,
        products: products.map((p) => ({
          id: p.id,
          quantity: p.quantity,
          ...(p.note !== undefined && { note: p.note }),
        })),
      };

      const response = (await postData(payload)) as CreateOrderResponse;

      if (!response || !response.id) {
        showToast(toast, 'error', 'Gagal membuat pesanan, silakan coba lagi');
        setIsSubmitting(false);
        return;
      }

      if (order && order.items && order.items.length > 0) {
        try {
          const totalValue = order.total || 0;
          const ecommerceItems = order.items.flatMap((item, itemIndex) =>
            item.products.map((product, productIndex) => {
              const price = product.price || 0;

              return {
                item_id: product.item_code?.toString() || '',
                item_name: product.title || '',
                item_brand: product.brand_name || '',
                affiliation: 'Sukanda Onelink',
                coupon: '',
                discount: 0,
                index: itemIndex * 100 + productIndex,
                item_category: product.category_name || '',
                item_category2: product.category_name || '',
                item_list_id: TRACKING_PAGES.ORDER_DETAIL,
                item_list_name: TRACKING_PAGES.ORDER_DETAIL,
                item_variant: product.type || '',
                location_id: warehouseIdToUse,
                price: price,
                quantity: (product.quantity ?? 0) / 1000 || 1,
              };
            })
          );

          trackEvent(TRACKING_EVENTS.BEGIN_CHECKOUT, {
            currency: 'IDR',
            value: totalValue,
            transaction_id: response.id,
            items: ecommerceItems,
            section_name: TRACKING_PAGES.ORDER_DETAIL,
            page_name: TRACKING_PAGES.ORDER_DETAIL,
          });
        } catch (error) {
          console.warn('BEGIN_CHECKOUT tracking failed:', error);
        }
      }

      const checkoutPath = isNewCheckoutEnabled ? '/checkout-new' : '/checkout';

      await navigateSafely(
        router.push({
          pathname: checkoutPath,
          query: { meta_id: response.id, warehouse: warehouseIdToUse },
        })
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Terjadi kesalahan';
      showToast(toast, 'error', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateToShoppingList = async () => {
    await navigateSafely(
      router.push({
        pathname: '/daftar-belanja/create',
        query: { ...router.query, type: 'order_detail' },
      })
    );
  };

  const handleNavigateHome = async () => {
    await navigateSafely(router.push('/'));
  };

  const handleNavigateMyOrder = async () => {
    await navigateSafely(router.push('/my-order'));
  };

  type CopyResult = { success: true } | { success: false; error: 'unsupported' | 'failed' };

  const hasClipboardAPI = (): boolean =>
    typeof navigator !== 'undefined' && Boolean(navigator.clipboard);

  const writeWithModernAPI = async (value: string): Promise<CopyResult> => {
    try {
      await navigator.clipboard.writeText(value);
      return { success: true };
    } catch {
      return { success: false, error: 'failed' };
    }
  };

  const writeWithFallback = (value: string): CopyResult => {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;';

    document.body.appendChild(textarea);
    textarea.select();

    const result = document.execCommand('copy')
      ? { success: true as const }
      : { success: false as const, error: 'failed' as const };

    document.body.removeChild(textarea);
    return result;
  };

  const copyToClipboard = async (value: string): Promise<CopyResult> => {
    if (hasClipboardAPI()) {
      return writeWithModernAPI(value);
    }
    return writeWithFallback(value);
  };

  const handleCopyToClipboard = async (value: string): Promise<boolean> => {
    const result = await copyToClipboard(value);

    if (result.success) {
      showToast(toast, 'success', 'Berhasil disalin ke clipboard');
      return true;
    } else {
      showToast(toast, 'error', 'Gagal menyalin ke clipboard');
      return false;
    }
  };

  return {
    handleBuyAgain,
    handleNavigateToShoppingList,
    handleNavigateHome,
    handleNavigateMyOrder,
    handleCopyToClipboard,
    isSubmitting,
  };
};
